/**
 * MuestrasPanel — Panel de muestras/tubos con estado y acciones.
 *
 * Requirements: M4.5.1, M4.5.3, M4.5.5, M4.5.6, M4.15.4
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { FlaskConical, Printer } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { useApiError } from "@/hooks/use-api-error";
import {
  useRegistrarToma,
  useRechazarMuestra,
  useMarcarInsuficiente,
  useRepetirMuestra,
  useGetEtiqueta,
  type AtencionDetalle,
  type MuestraListItem,
  type RechazarMuestraData,
} from "@/hooks/use-muestras";
import { getAccionesDisponibles } from "../utils/muestra-transitions";
import { MuestraEstadoBadge } from "./muestra-estado-badge";
import { RechazoMuestraDialog } from "./rechazo-muestra-dialog";

interface MuestrasPanelProps {
  atencion: AtencionDetalle;
  onGenerarMuestras: () => void;
  isGenerando: boolean;
}

export function MuestrasPanel({ atencion, onGenerarMuestras, isGenerando }: MuestrasPanelProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const [muestraRechazando, setMuestraRechazando] = useState<MuestraListItem | null>(null);

  const muestras = atencion.muestras ?? [];

  // Mutations con IDs dinámicos en el mutationFn
  const registrarTomaMutation = useRegistrarToma(atencion.id, {
    onSuccess: () => toast.success(t("muestras.muestras.toma_registrada")),
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const marcarInsuficienteMutation = useMarcarInsuficiente(atencion.id, {
    onSuccess: () => toast.success(t("muestras.muestras.marcada_insuficiente")),
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const repetirMutation = useRepetirMuestra(atencion.id, {
    onSuccess: () => toast.success(t("muestras.muestras.repeticion_creada")),
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const rechazarMutation = useRechazarMuestra(atencion.id, {
    onSuccess: () => {
      toast.success(t("muestras.muestras.rechazada"));
      setMuestraRechazando(null);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const etiquetaMutation = useGetEtiqueta({
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const handleRechazar = (data: RechazarMuestraData) => {
    if (!muestraRechazando) return;
    rechazarMutation.mutate({ muestraId: muestraRechazando.id, data });
  };

  return (
    <div className="space-y-4">
      {/* Generar muestras */}
      {muestras.length === 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">{t("muestras.muestras.sin_muestras")}</p>
          <Button onClick={onGenerarMuestras} disabled={isGenerando}>
            <FlaskConical className="h-4 w-4 mr-2" />
            {isGenerando ? t("common.loading") : t("muestras.muestras.generar")}
          </Button>
        </div>
      )}

      {/* Lista de muestras */}
      {muestras.map((muestra) => {
        const acciones = getAccionesDisponibles(muestra.estado);
        return (
          <Card key={muestra.id}>
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-mono text-sm font-medium">{muestra.codigo_barras}</p>
                  <p className="text-xs text-muted-foreground">
                    {muestra.tipo_contenedor_nombre ?? t("muestras.muestras.sin_contenedor")}
                    {muestra.volumen_requerido_ml && ` · ${muestra.volumen_requerido_ml} mL`}
                    {muestra.areas && muestra.areas.length > 0 && (
                      <> · {muestra.areas.join(", ")}</>
                    )}
                  </p>
                </div>
                <MuestraEstadoBadge
                  estado={muestra.estado}
                  estadoDisplay={muestra.estado_display}
                />
              </div>

              {/* Acciones */}
              <div className="flex flex-wrap gap-2">
                {acciones.includes("tomar") && (
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() =>
                      registrarTomaMutation.mutate({ muestraId: muestra.id, data: {} })
                    }
                    disabled={registrarTomaMutation.isPending}
                  >
                    {t("muestras.muestras.tomar")}
                  </Button>
                )}
                {acciones.includes("rechazar") && (
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setMuestraRechazando(muestra)}
                  >
                    {t("muestras.muestras.rechazar")}
                  </Button>
                )}
                {acciones.includes("insuficiente") && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => marcarInsuficienteMutation.mutate(muestra.id)}
                    disabled={marcarInsuficienteMutation.isPending}
                  >
                    {t("muestras.muestras.insuficiente")}
                  </Button>
                )}
                {acciones.includes("repetir") && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => repetirMutation.mutate(muestra.id)}
                    disabled={repetirMutation.isPending}
                  >
                    {t("muestras.muestras.repetir")}
                  </Button>
                )}
                {acciones.includes("etiqueta") && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => etiquetaMutation.mutate(muestra.id)}
                    disabled={etiquetaMutation.isPending}
                  >
                    <Printer className="h-3.5 w-3.5 mr-1" />
                    {t("muestras.muestras.etiqueta")}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Dialog de rechazo */}
      {muestraRechazando && (
        <RechazoMuestraDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) setMuestraRechazando(null);
          }}
          onConfirm={handleRechazar}
          isLoading={rechazarMutation.isPending}
        />
      )}
    </div>
  );
}
